/**
 * Utility functions for MIME type handling
 * Fetches suggestions from mime-db and provides helpers
 */

let mimeDbCache = null;
let mimeDbPromise = null;

/**
 * Fetch MIME type database from mime-db
 * @returns {Promise<Object>} MIME type database
 */
export const fetchMimeDb = async () => {
  if (mimeDbCache) {
    return mimeDbCache;
  }

  if (mimeDbPromise) {
    return mimeDbPromise;
  }

  mimeDbPromise = fetch('/mime-db/db.json')
    .then((res) => res.json())
    .then((data) => {
      mimeDbCache = data;
      mimeDbPromise = null;
      return data;
    })
    .catch((error) => {
      console.error('Failed to fetch MIME database:', error);
      mimeDbPromise = null;
      return {};
    });

  return mimeDbPromise;
};

/**
 * Get MIME types and their extensions
 * @returns {Promise<Array>} Array of {mimeType, extensions[]}
 */
export const getMimeTypes = async () => {
  const db = await fetchMimeDb();
  return Object.entries(db)
    .map(([mimeType, data]) => ({
      mimeType,
      extensions: data.extensions || [],
    }))
    .sort((a, b) => a.mimeType.localeCompare(b.mimeType));
};

/**
 * Get common MIME types grouped by category
 * @returns {Promise<Object>} Grouped MIME types {category: [{mimeType, extensions}]}
 */
export const getCommonMimeTypes = async () => {
  const allTypes = await getMimeTypes();

  const grouped = {
    'Image': allTypes.filter((t) => t.mimeType.startsWith('image/')),
    'Audio': allTypes.filter((t) => t.mimeType.startsWith('audio/')),
    'Video': allTypes.filter((t) => t.mimeType.startsWith('video/')),
    'Document': allTypes.filter((t) => t.mimeType.startsWith('application/') &&
      (t.mimeType.includes('pdf') || t.mimeType.includes('word') || t.mimeType.includes('sheet'))),
    'Archive': allTypes.filter((t) => t.mimeType.startsWith('application/') &&
      (t.mimeType.includes('zip') || t.mimeType.includes('tar') || t.mimeType.includes('gzip'))),
  };

  // Remove empty groups
  return Object.fromEntries(Object.entries(grouped).filter(([, types]) => types.length > 0));
};

/**
 * Get MIME type suggestions based on input
 * @param {string} input - User input (MIME type or extension)
 * @returns {Promise<string[]>} Array of matching MIME types
 */
export const getMimeTypeSuggestions = async (input) => {
  if (!input || input.length < 2) {
    return [];
  }

  const db = await fetchMimeDb();
  const lowerInput = input.toLowerCase();

  return Object.keys(db)
    .filter((mimeType) => mimeType.toLowerCase().includes(lowerInput))
    .slice(0, 10) // Limit to 10 suggestions
    .sort();
};

/**
 * Get extension suggestions based on input
 * @param {string} input - User input
 * @returns {Promise<string[]>} Array of matching extensions
 */
export const getExtensionSuggestions = async (input) => {
  if (!input || input.length < 1) {
    return [];
  }

  const db = await fetchMimeDb();
  const lowerInput = input.toLowerCase().replace(/^\./, ''); // Remove leading dot

  const suggestions = new Set();
  Object.values(db).forEach((data) => {
    if (data.extensions) {
      data.extensions
        .filter((ext) => ext.toLowerCase().startsWith(lowerInput))
        .forEach((ext) => suggestions.add(ext));
    }
  });

  return Array.from(suggestions).slice(0, 10).sort();
};

/**
 * Get MIME types for a given extension
 * @param {string} extension - File extension (with or without dot)
 * @returns {Promise<string[]>} Array of MIME types
 */
export const getMimeTypesForExtension = async (extension) => {
  const db = await fetchMimeDb();
  const ext = extension.toLowerCase().replace(/^\./, '');

  const mimeTypes = [];
  Object.entries(db).forEach(([mimeType, data]) => {
    if (data.extensions && data.extensions.includes(ext)) {
      mimeTypes.push(mimeType);
    }
  });

  return mimeTypes;
};

/**
 * Convert extensions to MIME types
 * @param {string[]} extensions - Array of file extensions
 * @returns {Promise<string[]>} Array of unique MIME types
 */
export const extensionsToMimeTypes = async (extensions) => {
  const mimeTypeSet = new Set();

  for (const ext of extensions) {
    const mimeTypes = await getMimeTypesForExtension(ext);
    mimeTypes.forEach((mt) => mimeTypeSet.add(mt));
  }

  return Array.from(mimeTypeSet).sort();
};

/**
 * Get common extensions by category
 * @returns {Object} Extensions grouped by category
 */
export const getCommonExtensions = () => {
  return {
    images: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'],
    documents: ['pdf', 'doc', 'docx', 'txt', 'rtf'],
    spreadsheets: ['xls', 'xlsx', 'csv'],
    archives: ['zip', 'rar', '7z', 'tar', 'gz'],
    audio: ['mp3', 'wav', 'flac', 'aac', 'ogg'],
    video: ['mp4', 'avi', 'mkv', 'mov', 'webm'],
  };
};

/**
 * Get extension for a MIME type (returns first/primary extension)
 * @param {string} mimeType - MIME type
 * @returns {Promise<string|null>} First matching extension or null
 */
export const getExtensionForMimeType = async (mimeType) => {
  const db = await fetchMimeDb();
  const data = db[mimeType];

  if (data && data.extensions && data.extensions.length > 0) {
    return data.extensions[0];
  }

  return null;
};

/**
 * Get all extensions for a MIME type
 * @param {string} mimeType - MIME type
 * @returns {Promise<string[]>} All matching extensions
 */
export const getExtensionsForMimeType = async (mimeType) => {
  const db = await fetchMimeDb();
  const data = db[mimeType];

  return (data && data.extensions) || [];
};

/**
 * Convert MIME type to human-readable label
 * @param {string} mimeType - MIME type
 * @returns {string} Human-readable label
 */
export const mimeTypeToLabel = (mimeType) => {
  const parts = mimeType.split('/');
  if (parts.length !== 2) return mimeType;

  const [type, subtype] = parts;
  const subtypeLabel = subtype
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return `${type.charAt(0).toUpperCase() + type.slice(1)}: ${subtypeLabel}`;
};

/**
 * Get recommended MIME types for common use cases
 * @returns {Object} Recommended types by category
 */
export const getRecommendedMimeTypes = () => {
  return {
    images: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
    documents: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    spreadsheets: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    archives: ['application/zip', 'application/x-rar-compressed', 'application/x-7z-compressed'],
    audio: ['audio/mpeg', 'audio/wav', 'audio/ogg'],
    video: ['video/mp4', 'video/mpeg', 'video/quicktime'],
  };
};
